/**
 * Supervision Section Types
 *
 * Types for clinical oversight, documentation review, and signing workflows.
 * Aligned with FHIR where applicable (Task, DocumentReference, Practitioner, etc.).
 */

export type DocumentReviewStatus =
  | 'draft'
  | 'edits-requested'
  | 'signed'
  | 'cosigned'
  | 'review-due'
  | 'amended'
  | 'completed';

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

/** Document types; 1:1 with Template Library categories. */
export type DocumentType = 'notes' | 'assessments' | 'treatment-planning' | 'forms' | 'other';

export const DocumentReviewStatusLabels: Record<DocumentReviewStatus, string> = {
  draft: 'Draft',
  'edits-requested': 'Edits Requested',
  signed: 'Signed',
  cosigned: 'Co-signed',
  'review-due': 'Review Due',
  amended: 'Amended',
  completed: 'Completed',
};

export const DocumentReviewStatusColors: Record<DocumentReviewStatus, string> = {
  draft: 'gray',
  'edits-requested': 'orange',
  signed: 'blue',
  cosigned: 'green',
  'review-due': 'yellow',
  amended: 'teal',
  completed: 'green',
};

export const RiskLevelLabels: Record<RiskLevel, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export const RiskLevelColors: Record<RiskLevel, string> = {
  low: 'gray',
  medium: 'yellow',
  high: 'orange',
  critical: 'red',
};

export const DocumentTypeLabels: Record<DocumentType, string> = {
  notes: 'Notes',
  assessments: 'Assessments',
  'treatment-planning': 'Treatment Planning',
  forms: 'Forms',
  other: 'Other',
};

/**
 * One row in the cosign notes table. When built from Medplum:
 * - status: from QuestionnaireResponse (qrToDocumentReviewStatus): supervisor sig → signed; provider sig → ready-to-sign; requested-edits → needs-edits; else draft
 * - documentType: from the referenced Questionnaire’s meta.tag (template-category), not from QR.subject or .subjectType
 */
export interface SupervisionQueueItem {
  id: string;
  documentType: DocumentType;
  /** When set, Type column uses {@link MissingDocumentFormTypeLabels} (aligned with Missing Documents). */
  missingDocumentFormType?: MissingDocumentFormType;
  title: string;
  clinicianId: string;
  clinicianName: string;
  clientId?: string;
  clientName?: string;
  status: DocumentReviewStatus;
  riskLevel?: RiskLevel;
  /** Min edit distance from previous note (same client + service line). Higher = more different = good (green). */
  editDistanceFromPreviousNote?: number;
  dueDate?: string;
  submittedAt?: string;
  /** IP address from which the provider signed/submitted the note (when available). */
  providerSignedFromIp?: string;
  isOverdue?: boolean;
  commentCount?: number;
  version?: number;
  /**
   * When set, the document content is loaded from the QuestionnaireResponse linked to this
   * Appointment (same tag-based linkage as Client Chart / Appointment page). Replace with
   * real appointment IDs from your Medplum data.
   */
  appointmentId?: string;
}

/** Queue item with optional document ID for review (missing session notes have appointmentId only). */
export type SupervisionQueueItemWithDocumentId = SupervisionQueueItem & {
  documentIdForReview?: string;
};

export interface Supervisee {
  id: string;
  name: string;
  role: string;
  unsignedCount: number;
  overdueCount?: number;
  lastActivityAt?: string;
}

/**
 * Anchor for line/section comments (GitHub PR–style).
 * Persisted with comments on QuestionnaireResponse via extension (see fhirNoteMapping REVIEW_COMMENTS_EXT_URL).
 */
export interface DocumentCommentAnchor {
  section?: string;
  /** 1-based line number (single line) — used by legacy plain-text mode */
  lineStart?: number;
  /** 1-based line number (range end; optional) — used by legacy plain-text mode */
  lineEnd?: number;
  /** Snippet of text the comment refers to (for display) */
  snippet?: string;
  /** linkId of the Questionnaire item this comment anchors to (QR-backed mode) */
  linkId?: string;
  /** Full slash-joined path for nested items, e.g. "group1/section2/question3" (QR-backed mode) */
  itemPath?: string;
}

/**
 * Single review comment. Stored on the document (QuestionnaireResponse) via extension
 * so any screen that loads the same QR can read comments (e.g. list view for count, history tab).
 */
export interface DocumentComment {
  id: string;
  authorId: string;
  authorName: string;
  authorRole: 'supervisor' | 'clinician';
  text: string;
  createdAt: string;
  resolvedAt?: string;
  anchor?: DocumentCommentAnchor;
}

/** Placeholder for note quality check (Levenshtein/AI). UI only; no implementation. */
export type NoteQualitySeverity = 'info' | 'warning' | 'error';

export interface NoteQualityIssue {
  id: string;
  /** 1-based line number(s) */
  lineStart: number;
  lineEnd?: number;
  message: string;
  severity: NoteQualitySeverity;
  /** Placeholder: 'levenshtein' | 'ai' when implemented */
  source: 'levenshtein' | 'ai';
  /** Optional suggested replacement text */
  suggestion?: string;
}

export interface AuditEntry {
  id: string;
  action: 'reviewed' | 'commented' | 'requested-edits' | 'signed';
  actorId: string;
  actorName: string;
  timestamp: string;
  details?: string;
}

/** Single entry for document history timeline (audit, comment, or edit). */
export type DocumentHistoryEntry =
  | {
      id: string;
      type: 'audit';
      timestamp: string;
      action: AuditEntry['action'];
      actorId: string;
      actorName: string;
      details?: string;
    }
  | {
      id: string;
      type: 'comment';
      timestamp: string;
      authorId: string;
      authorName: string;
      text: string;
      lineStart?: number;
      lineEnd?: number;
      snippet?: string;
      resolvedAt?: string;
    }
  | {
      id: string;
      type: 'edit';
      timestamp: string;
      actorId: string;
      actorName: string;
      description: string;
      summary?: string;
    };

export interface SupervisionFilters {
  /** Selected clinician IDs; empty = all. */
  clinicianIds: string[];
  /** Selected document types; empty = all. */
  documentTypes: DocumentType[];
  /** Selected statuses; empty = all. */
  status: DocumentReviewStatus[];
  riskLevel: RiskLevel | 'all';
  search: string;
  sortBy: 'oldest' | 'newest' | 'risk' | 'clinician' | 'due' | 'patient';
  sortDirection: 'asc' | 'desc';
}

export const defaultSupervisionFilters: SupervisionFilters = {
  clinicianIds: [],
  documentTypes: [],
  status: [],
  riskLevel: 'all',
  search: '',
  sortBy: 'oldest',
  sortDirection: 'asc',
};

/** Layout/style variants for the supervision section */
export type SupervisionLayoutStyle = 'clinical-minimal' | 'data-dense' | 'card-modern';

/** Missing Documents: items from supervisees that are currently missing. */
export type MissingDocumentCategory = 'missing-notes' | 'treatment-plans' | 'forms';

export const MissingDocumentCategoryLabels: Record<MissingDocumentCategory, string> = {
  'missing-notes': 'Progress Notes',
  'treatment-plans': 'Treatment Plans',
  forms: 'Forms',
};

/** Form sub-types when category is "forms" (includes Discharge Summary). */
export type MissingDocumentFormType = 'roi' | 'custody-agreement' | 'intake' | 'discharge-summary';

export const MissingDocumentFormTypeLabels: Record<MissingDocumentFormType, string> = {
  roi: 'ROI',
  'custody-agreement': 'Custody Agreement',
  intake: 'Intake Form',
  'discharge-summary': 'Discharge Summary',
};

/** Same Type column logic as Missing Documents (category + optional form subtype). */
export function getMissingDocumentRowTypeLabel(
  category: MissingDocumentCategory,
  formType?: MissingDocumentFormType,
): string {
  if (category === 'forms' && formType) {
    return MissingDocumentFormTypeLabels[formType];
  }
  return MissingDocumentCategoryLabels[category];
}

/**
 * Unsigned Notes / cosign queue Type column: same strings as Missing Documents when the
 * template maps to a missing-doc category or inferred form subtype; otherwise
 * {@link DocumentTypeLabels} (e.g. measures not modeled on supervision Tasks).
 */
export function getSupervisionQueueTypeLabel(item: {
  documentType: DocumentType;
  missingDocumentFormType?: MissingDocumentFormType;
}): string {
  if (item.missingDocumentFormType) {
    return MissingDocumentFormTypeLabels[item.missingDocumentFormType];
  }
  if (item.documentType === 'notes') {
    return getMissingDocumentRowTypeLabel('missing-notes');
  }
  if (item.documentType === 'treatment-planning') {
    return getMissingDocumentRowTypeLabel('treatment-plans');
  }
  if (item.documentType === 'forms') {
    return getMissingDocumentRowTypeLabel('forms');
  }
  return DocumentTypeLabels[item.documentType] ?? DocumentTypeLabels.other;
}

export type MissingDocumentStatus = 'overdue' | 'due-soon' | 'missing' | 'unsigned' | 'edits-requested';

export const MissingDocumentStatusLabels: Record<MissingDocumentStatus, string> = {
  overdue: 'Overdue',
  'due-soon': 'Due soon',
  missing: 'Missing',
  unsigned: 'Unsigned',
  'edits-requested': 'Edits Requested',
};

export interface MissingDocumentItem {
  id: string;
  category: MissingDocumentCategory;
  /** Set when category is 'forms'. */
  formType?: MissingDocumentFormType;
  clinicianId: string;
  clinicianName: string;
  clientId?: string;
  clientName?: string;
  /**
   * When category is `missing-notes`, optional FHIR Appointment id for navigation to the
   * appointment page (also derivable from synthetic ids `appt-missing-note-{appointmentId}`).
   */
  appointmentId?: string;
  /** Session or service date when applicable. */
  sessionDate?: string;
  dueDate?: string;
  status: MissingDocumentStatus;
  /** Short description (e.g. "Progress note for 1/15 session"). */
  description?: string;
  documentId?: string;
}

/** Appointment id for progress-note rows: explicit field or inferred from `appt-missing-note-*` id. */
export function resolveMissingDocumentAppointmentId(item: MissingDocumentItem): string | undefined {
  if (item.appointmentId) return item.appointmentId;
  const m = item.id.match(/^appt-missing-note-(.+)$/);
  return m?.[1];
}

export function canNavigateMissingDocumentRow(item: MissingDocumentItem): boolean {
  if (item.category === 'forms' || item.category === 'treatment-plans') {
    return !!item.clientId;
  }
  return !!resolveMissingDocumentAppointmentId(item) || !!item.clientId;
}

export type MissingDocumentSortBy =
  | 'client'
  | 'sessionDate'
  | 'dueDate'
  | 'clinician'
  | 'type'
  | 'status';

export interface MissingDocumentFilters {
  search: string;
  /** Selected provider IDs; empty = all. */
  providerIds: string[];
  /** Selected document types (categories); empty = all. */
  types: MissingDocumentCategory[];
  formType: MissingDocumentFormType | 'all';
  sortBy: MissingDocumentSortBy;
  sortDirection: 'asc' | 'desc';
}

export const defaultMissingDocumentFilters: MissingDocumentFilters = {
  search: '',
  providerIds: [],
  types: [],
  formType: 'all',
  sortBy: 'dueDate',
  sortDirection: 'asc',
};
